/**
 * SME Portal — Upload Assets page.
 * SMEs can upload assets that go through admin/EM approval before release.
 */
import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import { Upload, CheckCircle, HourglassEmpty, Cancel } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface UploadedAsset {
  id: string;
  assessment_type: string;
  asset_class: string;
  filename: string;
  description: string | null;
  status: string;
  review_notes: string | null;
  created_at: string | null;
}

const ASSET_CLASSES = ['questions', 'sales_pitch', 'report_template', 'tool'];

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <HourglassEmpty fontSize="small" />,
  approved: <CheckCircle fontSize="small" />,
  rejected: <Cancel fontSize="small" />,
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const UploadAssets: React.FC = () => {
  const [assets, setAssets] = useState<UploadedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    assessment_type: '',
    asset_class: '',
    description: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/sme/assets/uploads');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load assets');
      setAssets(data.assets);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssets(); }, []);

  const handleSubmit = async () => {
    if (!formData.assessment_type || !formData.asset_class || !selectedFile) {
      setError('Assessment type, asset class, and file are required');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', selectedFile);
      body.append('assessment_type', formData.assessment_type);
      body.append('asset_class', formData.asset_class);
      body.append('description', formData.description);

      const res = await authFetch('/api/sme/assets/upload', {
        method: 'POST',
        body,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess('Asset uploaded successfully. Pending review in Review SME Assets.');
        setDialogOpen(false);
        setFormData({ assessment_type: '', asset_class: '', description: '' });
        setSelectedFile(null);
        fetchAssets();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch {
      setError('Failed to upload asset');
    } finally {
      setSubmitting(false);
    }
  };

  const resetDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setSelectedFile(null);
    setFormData({ assessment_type: '', asset_class: '', description: '' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Upload Assets
          </Typography>
          <RefreshButton onRefresh={fetchAssets} loading={loading} lastRefreshed={lastRefreshed} />
          <Typography variant="body2" color="text.secondary">
            Submit KPMG materials to Review SME Assets. Once approved by an EM/EL, Super Admin or Content Manager,
            client-visible materials appear in the client Asset Library as view-only.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Upload />} onClick={() => setDialogOpen(true)}>
          Upload New Asset
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : assets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">You haven't uploaded any assets yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Review Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.filename}</Typography>
                    {asset.description && (
                      <Typography variant="caption" color="text.secondary">{asset.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell><Chip label={asset.asset_class} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={asset.status}
                      size="small"
                      color={STATUS_COLORS[asset.status] || 'default'}
                      icon={STATUS_ICONS[asset.status] as React.ReactElement || undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {asset.review_notes || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Asset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit materials for review by an EM/EL, Super Admin or Content Manager.
            Supported: PDF, DOCX, PPTX, XLSX, TXT, CSV, JSON, PNG and JPEG, up to 25 MB per file.
          </Typography>
          <TextField
            fullWidth
            label="Assessment Type"
            placeholder="e.g. m365_copilot_readiness"
            value={formData.assessment_type}
            onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            select
            label="Asset Class"
            value={formData.asset_class}
            onChange={(e) => setFormData({ ...formData, asset_class: e.target.value })}
            sx={{ mb: 2 }}
            required
          >
            {ASSET_CLASSES.map((cls) => (
              <MenuItem key={cls} value={cls}>{cls}</MenuItem>
            ))}
          </TextField>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<Upload />}
            sx={{ mb: 2, justifyContent: 'flex-start', textTransform: 'none' }}
          >
            {selectedFile ? selectedFile.name : 'Choose File *'}
            <input
              type="file"
              accept=".pdf,.docx,.pptx,.xlsx,.txt,.csv,.json,.png,.jpg,.jpeg"
              hidden
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </Button>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={resetDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Upload />}
          >
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadAssets;
